import { Box, Button, Flex, Image, Modal, Paper, Text, Title } from '@mantine/core';
import dayjs from 'dayjs';
import type { FC } from 'react';

import { env } from '../../env.ts';
import { useI18n } from '../../hooks/useI18n.ts';
import { useIncompleteEventItem } from '../hooks/useIncompleteEventItem.ts';

const formatTime = (time: Date | undefined) => {
  return time ? dayjs(time).format('ddd DD/MM hh:mm') : 'Not specified';
};

interface IncompleteEventDeleteProps {
  incompleteEventId?: string;
  onClose: () => void;
}

export const IncompleteEventDelete: FC<IncompleteEventDeleteProps> = ({ incompleteEventId, onClose }) => {
  const { t } = useI18n();

  const { query, removeMutation } = useIncompleteEventItem(incompleteEventId ?? '');

  const eventData = query.data;

  const onRemove = () => {
    removeMutation.mutate(undefined, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  const onCancel = () => {
    onClose();
  };

  return (
    <Modal
      centered
      opened
      size="lg"
      title={
        <Title component="span" order={3}>
          {t('event-delete_modal-title') ?? 'Remove event'}
        </Title>
      }
      onClose={onClose}
    >
      {incompleteEventId && eventData && (
        <Box>
          <Text mb="xs">{t('event-delete_removing-event--incomplete') ?? 'You are removing incomplete event:'}</Text>
          <Paper withBorder p="xxs">
            <Flex gap="xxs">
              {eventData.image && (
                <Image
                  alt={eventData.name || ''}
                  crossOrigin="anonymous"
                  h={80}
                  radius="xs"
                  src={`${env.VITE_DIRECTUS_API_URL}/assets/${eventData.image}?width=160&height=160&fit=cover`}
                  w={80}
                />
              )}
              <Box>
                <Title mb="xxxs" order={5}>
                  {(eventData.name || t('event-delete_event-name-not-specified')) ?? 'Event name not specified'}
                </Title>
                <Text size="xxs">
                  <Text span fw={500} size="xxs">
                    {t('event-delete_from') ?? 'From:'}
                  </Text>{' '}
                  {formatTime(eventData.start)}
                </Text>
                <Text size="xxs">
                  <Text span fw={500} size="xxs">
                    {t('event-delete_to') ?? 'To:'}
                  </Text>{' '}
                  {formatTime(eventData.end)}
                </Text>
              </Box>
            </Flex>
          </Paper>
          <Text mt="xxs" size="xxs">
            {t('event-delete_confirm-message') ?? 'Are you sure? Confirm to delete'}
          </Text>
          <Flex gap="xxxs" justify="flex-end" mt="xxs">
            <Button color="gray" radius="xl" size="md" variant="subtle" onClick={onCancel}>
              {t('event-delete_cancel-button') ?? 'Cancel'}
            </Button>
            <Button color="red" loading={removeMutation.isPending} radius="xl" size="md" onClick={onRemove}>
              {t('event-delete_delete-button') ?? 'Delete'}
            </Button>
          </Flex>
        </Box>
      )}
    </Modal>
  );
};
